<template>
    <div class="relative w-full">
        <div
            data-testid="datepicker-trigger"
            :data-open="isModalOpen ? 'true' : 'false'"
            @click="toggleModal"
            class="flex items-center justify-between px-3 py-2 border rounded-lg cursor-pointer transition-all duration-200"
            :class="{
                'cursor-not-allowed bg-gray-100 text-gray-400 border-gray-300': disabled,
                'border-blue-500 ring-2 ring-blue-100': isModalOpen && !disabled,
                'border-gray-300 hover:border-blue-400': !isModalOpen && !disabled,
            }"
            :aria-disabled="disabled"
        >
            <span v-if="iconFront" class="flex-none mr-2">
                <AppIcon
                    :icon="iconFront"
                    class="w-5 h-5"
                    :class="disabled ? 'text-gray-300' : 'text-gray-600'"
                />
            </span>
            <div class="flex flex-col flex-grow">
                <label v-if="label" :class="labelClass">{{ label }}</label>
                <span
                    :class="[
                        displayedLabelClass,
                        disabled ? 'text-gray-400' : '',
                    ]"
                >
                    {{ displayedDate }}
                </span>
            </div>
            <span v-if="iconBack" class="ml-2">
                <AppIcon
                    :icon="iconBack"
                    class="w-5 h-5"
                    :class="{
                        'text-gray-400': disabled,
                        'text-gray-600': !disabled,
                    }"
                />
            </span>
        </div>

        <Teleport :to="teleport ? 'body' : null" :disabled="!teleport">
            <transition name="fade">
                <div
                    v-if="isModalOpen && !disabled && teleport"
                    class="fixed inset-0 z-[999998] bg-black bg-opacity-50 md:hidden"
                    @click="closeModal"
                ></div>
            </transition>
            
            <transition name="slide-down">
                <div
                    v-if="isModalOpen && !disabled"
                    data-testid="datepicker-panel"
                    :class="['mt-2', teleport ? 'fixed z-[999999] w-max' : 'absolute z-[1100] w-max max-w-[95vw]', positionClass]"
                    :style="dropdownStyle"
                    @click.stop
                >
                    <BaseDate
                        ref="datePicker"
                        :selectedDate="normalizedModel"
                        :checkInDate="checkInDate"
                        :position="position"
                        :applyAvailabilityWindow="applyAvailabilityWindow"
                        :availabilityWindowDays="availabilityWindowDays"
                        :approvedBookingRequestDate="approvedBookingRequestDate"
                        :excludeStart="excludeStart"
                        :excludeEnd="excludeEnd"
                        @dateSelected="handleDateSelected"
                        @focus-month="focusOnCheckInMonth"
                        @close="closeModal"
                        class="!static !mt-0 w-[95vw] sm:w-[360px] max-w-sm"
                        :room-id="roomIdState"
                        :room-type-id="roomTypeIdState"
                        :property-id="propertyIdState"
                    />
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script>
import BaseDate from "./BaseDate.vue";
import { format } from "date-fns";

export default {
    name: "BaseDatePicker",
    components: { BaseDate },
    props: {
        label: { type: String, default: "" },
        labelClass: {
            type: String,
            default: "text-sm text-blue-500 font-medium",
        },
        placeholder: { type: String, default: "Select date" },
        iconBack: { type: String, default: "mdi:calendar" },
        iconFront: { type: String, default: "" },
        modelValue: { type: [Date, String, Number, null], default: null },
        position: {
            type: String,
            default: "right",
            validator: (v) => ["left", "right"].includes(v),
        },
        checkInDate: { type: [Date, String, Number, null], default: null },
        disabled: { type: Boolean, default: false },
        roomId: { type: String, default: null },
        roomTypeId: { type: String, default: null },
        propertyId: { type: [String, Number], default: null },
        applyAvailabilityWindow: { type: Boolean, default: false },
        availabilityWindowDays: { type: Number, default: null },
        approvedBookingRequestDate: { type: [Date, String, Number, null], default: null },
        excludeStart: { type: [Date, String, Number, null], default: null },
        excludeEnd: { type: [Date, String, Number, null], default: null },
        teleport: { type: Boolean, default: false },
        isOpen: { type: Boolean, default: false },
        alignWithGroupLeft: { type: Boolean, default: false },
    },
    data() {
        return {
            isModalOpen: false,
            roomIdState: this.roomId,
            roomTypeIdState: this.roomTypeId,
            propertyIdState: this.propertyId,
            teleportStyle: {},
            panelStyle: {},
        };
    },
    watch: {
        roomId(v) {
            this.roomIdState = v;
        },
        roomTypeId(v) {
            this.roomTypeIdState = v;
        },
        propertyId(v) {
            this.propertyIdState = v;
        },
        isOpen(v) {
            this.isModalOpen = v;
            if (v) {
                this.$nextTick(() => {
                    this.updatePanelPosition();
                    this.addPositionListeners();
                    this.focusCalendarToSelectedDate();
                });
            } else if (!v) {
                this.removeEventListeners();
                this.panelStyle = {};
            }
        },
        isModalOpen(v) {
            if (v !== this.isOpen) {
                this.$emit(v ? "open" : "close");
            }
        },
    },
    computed: {
        normalizedModel() {
            return this.normalizeDate(this.modelValue) || this.today();
        },
        displayedDate() {
            const d = this.normalizeDate(this.modelValue);
            return d ? format(d, "dd MMM yyyy") : this.placeholder;
        },
        displayedLabelClass() {
            return this.normalizeDate(this.modelValue)
                ? "text-sm text-gray-700"
                : "text-sm text-gray-500";
        },
        dropdownStyle() {
            return this.teleport ? this.teleportStyle : this.panelStyle;
        },
        positionClass() {
            return this.position === "right"
                ? "left-auto right-0"
                : "left-0 right-auto";
        },
    },
    methods: {
        focusCalendarToSelectedDate() {
            const selectedDate = this.normalizeDate(this.modelValue);
            if (!selectedDate) return;
            if (this.$refs.datePicker?.setCurrentMonth) {
                this.$refs.datePicker.setCurrentMonth(selectedDate);
            }
        },
        setCurrentMonth(date) {
            if (this.$refs.datePicker && date) {
                this.$refs.datePicker.setCurrentMonth(date);
            }
        },
        toggleModal() {
            if (this.disabled) return;
            
            if (this.isModalOpen) {
                this.closeModal();
            } else {
                this.isModalOpen = true;
                this.$emit("open");

                this.$nextTick(() => {
                    this.updatePanelPosition();
                    this.addPositionListeners();
                    this.focusCalendarToSelectedDate();
                });
            }
        },
        updatePanelPosition() {
            if (this.teleport) {
                this.updateTeleportPosition();
                return;
            }

            this.panelStyle = {};
            if (!this.isModalOpen) return;

            // Keep dropdown inside viewport when rendered absolutely (non-teleport mode).
            if (!this.alignWithGroupLeft && this.$el) {
                const hostRect = this.$el.getBoundingClientRect();
                const safeMargin = 10;
                const popupWidth = Math.min(360, window.innerWidth - safeMargin * 2);
                let leftPosition =
                    this.position === "right"
                        ? hostRect.right - popupWidth
                        : hostRect.left;

                if (leftPosition + popupWidth > window.innerWidth - safeMargin) {
                    leftPosition = window.innerWidth - popupWidth - safeMargin;
                }
                if (leftPosition < safeMargin) {
                    leftPosition = safeMargin;
                }

                this.panelStyle = {
                    left: `${leftPosition - hostRect.left}px`,
                    right: "auto",
                };
                return;
            }

            if (!this.alignWithGroupLeft) return;
            if (window.innerWidth < 768) return;

            const host = this.$el;
            const group = host?.closest?.("[data-datepicker-group='reschedule']");
            if (!host || !group) return;

            const hostRect = host.getBoundingClientRect();
            const groupRect = group.getBoundingClientRect();
            const offsetLeft = Math.max(0, hostRect.left - groupRect.left);
            this.panelStyle = {
                left: `-${offsetLeft}px`,
                right: "auto",
            };
        },
        updateTeleportPosition() {
            if (!this.$el || !this.isModalOpen) return;
            
            if (window.innerWidth < 768) {
                // Mobile centering
                this.teleportStyle = {
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                };
                return;
            }

            const rect = this.$el.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const popupHeight = 400; // estimated
            const popupWidth = 360; // from sm:w-[360px]
            
            let topPosition = rect.bottom;
            if (spaceBelow < popupHeight && rect.top > popupHeight) {
                topPosition = rect.top - popupHeight - 16;
            }
            
            let leftPosition = rect.left;
            // Prevent overflowing the right edge of the screen
            if (leftPosition + popupWidth > window.innerWidth - 20) {
                leftPosition = rect.right - popupWidth;
            }
            // Prevent overflowing the left edge
            if (leftPosition < 10) {
                leftPosition = 10;
            }
            
            this.teleportStyle = {
                top: `${topPosition}px`,
                left: `${leftPosition}px`,
                transform: 'none',
            };
        },
        removeEventListeners() {
            window.removeEventListener('scroll', this.updateTeleportPosition, true);
            window.removeEventListener('resize', this.updatePanelPosition);
        },
        addPositionListeners() {
            if (this.teleport) {
                window.addEventListener('scroll', this.updateTeleportPosition, true);
            }
            window.addEventListener('resize', this.updatePanelPosition);
        },
        closeModal() {
            this.isModalOpen = false;
            this.$emit("close");
            this.removeEventListeners();
        },
        handleDateSelected(selectedDate) {
            const d = this.normalizeDate(selectedDate);
            this.$emit("update:modelValue", d || this.today());
            this.isModalOpen = false;
        },
        handleClickOutside(event) {
            if (this.isModalOpen && !this.$el.contains(event.target)) {
                this.isModalOpen = false;
            }
        },

        // ===== Helpers =====
        normalizeDate(v) {
            if (v === null || v === undefined || v === "") return null;
            const d = v instanceof Date ? v : new Date(v);
            return isNaN(d) ? null : d;
        },
        today() {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            return d;
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
        
        if (this.isOpen) {
            this.isModalOpen = true;
        }

    },
    beforeUnmount() {
        document.removeEventListener("click", this.handleClickOutside);
        this.removeEventListeners();
    },
};
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}
.slide-down-enter,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
.z-50 {
    z-index: 50;
}
.cursor-not-allowed {
    cursor: not-allowed;
}
.bg-gray-100 {
    background-color: #f3f4f6;
}
.text-gray-400 {
    color: #9ca3af;
}
</style>
